/**
 * Shared article-framing helpers for V6 effective-bias integration.
 *
 * Signal policy:
 *   source side       = publisher cohort (categorySpecified) — not handled here
 *   article framing   = how this article presents the issue (this module)
 *   sentiment         = tone — not handled here
 *   coverage presence = whether a cohort covered it — not handled here
 *
 * Rules:
 * 1. Prefer broadSide when present.
 * 2. broadSide === 'insufficient' is non-directional (never Center).
 * 3. Numeric 35/65 thresholds only when canonical broadSide is absent (legacy).
 * 4. Never threshold an abstained EV score.
 * 5. Unknown/missing → no invented political side.
 * 6. Display strings (label/framingLabel) are for rendering only — never branch on them.
 */

export const SOURCE_MIX_TOOLTIP =
  'Source mix: which outlets covered this, grouped by publisher lean. This is not a rating of each article.';

export const FRAMING_TOOLTIP =
  'How this article presents the issue, based on BarbershopTalk\'s article-level political framing methodology.';

export const INSUFFICIENT_TOOLTIP =
  'The article does not contain enough directional evidence for a responsible left, center, or right rating.';

export const SENTIMENT_TOOLTIP =
  'Tone: how positive or negative the coverage is. Independent of political lean.';

export const INSUFFICIENT_COPY =
  'Insufficient Evidence';

/** Model abstention line for Barbershop Take and related surfaces (never say "AI"). */
export const INSUFFICIENT_MODEL_COPY =
  'Our model found too little directional evidence';

export const ABOUT_RATING_TITLE = 'About this rating';

export const ABOUT_RATING_BODY =
  'The Bias Barometer rates the political lean of this individual article. It does not assign a permanent political label to the publisher or determine whether the article is factually correct. The bias score shows spectrum position, not confidence.';

/** Friendly display labels for broadSide values (not for branching). */
export const BROAD_SIDE_DISPLAY = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
  insufficient: 'Insufficient Evidence',
};

/**
 * Chip / token styles keyed by broadSide.
 * Insufficient is always gray and non-directional.
 */
export const FRAMING_LEAN_TOKENS = {
  left: {
    color: '#3730a3',
    bg: '#eef2ff',
    border: 'rgba(55, 48, 163, 0.18)',
    label: 'Left-framed',
  },
  center: {
    color: '#374151',
    bg: '#f9fafb',
    border: 'rgba(55, 65, 81, 0.18)',
    label: 'Center-framed',
  },
  right: {
    color: '#881337',
    bg: '#fff1f2',
    border: 'rgba(136, 19, 55, 0.18)',
    label: 'Right-framed',
  },
  insufficient: {
    color: '#4b5563',
    bg: '#f3f4f6',
    border: 'rgba(75, 85, 99, 0.2)',
    label: 'Insufficient Evidence',
  },
};

/**
 * Font Awesome icon classes for framing sides.
 * insufficient → neutral question icon (never balance-scale / Center).
 */
export const FRAMING_ICONS = {
  left: 'fa fa-light fa-democrat',
  center: 'fa fa-light fa-balance-scale',
  right: 'fa fa-light fa-republican',
  insufficient: 'fa fa-light fa-circle-question',
};

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeBroadSide(value) {
  if (value == null) return null;
  const s = String(value).trim().toLowerCase();
  if (s === 'left' || s === 'center' || s === 'right' || s === 'insufficient') {
    return s;
  }
  return null;
}

/**
 * True when the response is an explicit V6 (or effective) abstention.
 */
export function isFramingAbstained(prediction) {
  if (!prediction || typeof prediction !== 'object') return false;
  if (prediction.abstained === true) return true;
  if (normalizeBroadSide(prediction.broadSide) === 'insufficient') return true;
  return false;
}

/**
 * True when needle/score may be shown.
 * Abstained EV scores must never drive the meter.
 */
export function isFramingDirectional(prediction) {
  if (!prediction || typeof prediction !== 'object') return false;
  if (isFramingAbstained(prediction)) return false;
  if (prediction.isDirectional === true) return true;
  // Legacy rows often lack isDirectional but have a score and no broadSide.
  if (
    prediction.isDirectional == null &&
    prediction.broadSide == null &&
    toFiniteNumber(prediction.score) != null
  ) {
    return true;
  }
  return false;
}

/**
 * Legacy-only threshold side when broadSide is absent.
 * Must never run on abstained scores.
 *
 * @returns {'left'|'center'|'right'|null}
 */
export function legacyThresholdSide(score) {
  const n = toFiniteNumber(score);
  if (n == null) return null;
  if (n >= 65) return 'right';
  if (n <= 35) return 'left';
  return 'center';
}

/**
 * Resolve canonical framing side for icons/chips.
 *
 * @param {object|null|undefined} prediction
 *   Expected keys: broadSide, score, isDirectional, abstained
 * @returns {'left'|'center'|'right'|'insufficient'|null}
 */
export function resolveFramingSide(prediction) {
  if (!prediction || typeof prediction !== 'object') return null;

  if (isFramingAbstained(prediction)) {
    return 'insufficient';
  }

  const fromBroad = normalizeBroadSide(prediction.broadSide);
  if (fromBroad) {
    return fromBroad;
  }

  // Threshold fallback ONLY when canonical broadSide is absent (legacy shape).
  // Never invent a side when there is no score either.
  return legacyThresholdSide(prediction.score);
}

/**
 * Token for chip styling from broadSide (or resolved side).
 * Unknown → null (hide chip).
 */
export function getFramingLeanToken(broadSideOrPrediction) {
  let side = null;
  if (broadSideOrPrediction != null && typeof broadSideOrPrediction === 'object') {
    side = resolveFramingSide(broadSideOrPrediction);
  } else {
    side = normalizeBroadSide(broadSideOrPrediction);
  }
  if (!side) return null;
  return FRAMING_LEAN_TOKENS[side] ?? null;
}

/**
 * Icon class for a resolved framing side.
 */
export function getFramingIconClass(side) {
  const normalized = normalizeBroadSide(side);
  if (!normalized) return null;
  return FRAMING_ICONS[normalized] ?? null;
}

/**
 * Title-case a friendly framing label for display (e.g. "leans right" → "Leans Right").
 * Display only; never use for branching.
 */
export function titleCaseFramingLabel(label) {
  if (label == null) return null;
  const s = String(label).trim();
  if (!s) return null;
  return s.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

/**
 * User-facing framing badge text from a prediction / article member.
 * Prefers framingLabel, then label; insufficient gets fixed copy.
 */
export function getFramingDisplayLabel(prediction) {
  if (!prediction || typeof prediction !== 'object') return null;
  if (isFramingAbstained(prediction)) {
    return BROAD_SIDE_DISPLAY.insufficient;
  }
  const friendly =
    prediction.framingLabel ||
    prediction.label ||
    null;
  if (friendly != null && String(friendly).trim() !== '') {
    return titleCaseFramingLabel(friendly);
  }
  const side = resolveFramingSide(prediction);
  if (side && BROAD_SIDE_DISPLAY[side]) {
    return BROAD_SIDE_DISPLAY[side];
  }
  return null;
}

/**
 * Whether an article/member object carries usable framing fields.
 * List feeds currently omit these — return false and show no framing chip.
 */
export function hasFramingFields(article) {
  if (!article || typeof article !== 'object') return false;
  if (article.broadSide != null && String(article.broadSide).trim() !== '') return true;
  if (article.abstained === true) return true;
  if (article.framingLabel != null && String(article.framingLabel).trim() !== '') return true;
  if (article.isDirectional === true || article.isDirectional === false) return true;
  if (article.predictionSource != null && String(article.predictionSource).trim() !== '') return true;
  return false;
}

/**
 * Screen-reader label for a framing prediction.
 */
export function framingAriaLabel(prediction) {
  if (!prediction || typeof prediction !== 'object') {
    return 'Article lean: unavailable';
  }
  if (isFramingAbstained(prediction)) {
    return 'Article lean: insufficient evidence';
  }

  const display = getFramingDisplayLabel(prediction) || 'unknown';
  const score = toFiniteNumber(prediction.score);
  const parts = [`Article lean: ${display}`];

  if (isFramingDirectional(prediction) && score != null) {
    parts.push(`bias score ${Math.round(score)} of 100`);
  }

  const source = prediction.predictionSource
    ? String(prediction.predictionSource).toLowerCase()
    : null;
  if (source === 'v6') {
    parts.push('BarbershopTalk model');
  } else if (source === 'legacy') {
    parts.push('legacy assessment');
  }

  return parts.join(', ');
}

/**
 * Quiet provenance line for secondary hints (avoid raw model-version jargon in hero UI).
 */
export function framingProvenanceText(prediction) {
  if (!prediction || typeof prediction !== 'object') return '';
  const source = prediction.predictionSource
    ? String(prediction.predictionSource).toLowerCase()
    : null;
  if (source === 'v6') return 'BarbershopTalk model';
  if (source === 'legacy') return 'Legacy assessment';
  return '';
}

/**
 * Convert a saved legacy biasMin/biasMax preset to categorical framingSides
 * for display only. Returns null for full range or unrecognized custom ranges
 * (do not guess; do not overwrite saved prefs).
 *
 * Mapping (matches backend conversion SQL):
 *   0–40  → ['left']
 *   40–60 → ['center']
 *   60–100 → ['right']
 *   0–100 / other → null (no filter / custom)
 */
export function legacyBiasRangeToFramingSides(biasMin, biasMax) {
  const min = toFiniteNumber(biasMin);
  const max = toFiniteNumber(biasMax);
  if (min == null || max == null) return null;

  const lo = Math.min(min, max);
  const hi = Math.max(min, max);

  if (lo === 0 && hi === 100) return null;
  if (lo === 0 && hi <= 40) return ['left'];
  if (lo >= 40 && hi <= 60) return ['center'];
  if (lo >= 60 && hi === 100) return ['right'];
  return null; // unknown custom range — do not invent
}

/**
 * True when a numeric bias range is a known legacy preset (or full range).
 */
export function isKnownLegacyBiasPreset(biasMin, biasMax) {
  const min = toFiniteNumber(biasMin);
  const max = toFiniteNumber(biasMax);
  if (min == null || max == null) return false;
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  if (lo === 0 && hi === 100) return true;
  if (lo === 0 && hi <= 40) return true;
  if (lo >= 40 && hi <= 60) return true;
  if (lo >= 60 && hi === 100) return true;
  return false;
}

/**
 * Normalize a framingSides array from the API (null/empty → []).
 * Only keeps left|center|right.
 */
export function normalizeFramingSides(raw) {
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  for (const item of list) {
    if (item == null) continue;
    const n = String(item).trim().toLowerCase();
    if ((n === 'left' || n === 'center' || n === 'right') && !out.includes(n)) {
      out.push(n);
    }
  }
  return out;
}
